/* ==================================================================
   THE SALON — drifting gallery behaviour
   ------------------------------------------------------------------
   No GSAP, no ScrollTrigger, no pin. The stage is CSS `position:
   sticky`, so there is nothing to measure and nothing to refresh —
   it cannot desync from the entry gate the way a pinned timeline can.

   One rAF loop drives everything: scroll drift, cursor lean, dust,
   the thread, and the index.

   ==================================================================
   WHY THE FIELD IS LAID OUT IN COLUMNS
   ------------------------------------------------------------------
   Portraits used to sit at hand-tuned scatter positions, each with its
   own parallax depth in a 0.78-1.04 band. That cannot hold. The screen
   gap between two cards is

       gap(p) = gap(0) - p * R * (d_a - d_b)

   where R is the drift range. R was 4115px at twenty-four cards, so a
   depth difference of 0.26 ate 1070px of separation — far more than
   any gap the layout could offer. Measured on the live site, four
   pairs collided, the worst at 71% overlap.

   It also could not be fixed by retuning the numbers: R grows with the
   card count, so the erosion grows as the catalogue does, and this
   shop adds an animal a day.

   The rule that actually holds: cards that can ever overlap
   HORIZONTALLY must share a depth. Then (d_a - d_b) is zero and their
   separation is invariant under any scroll, any R, forever.

   So the field is columns:
     * columns are horizontally disjoint, with real gutters, so cards
       in different columns cannot overlap whatever their depths;
     * one depth per column, so cards within a column cannot converge.

   Overlap is therefore impossible by construction rather than by
   tuning — at any card count, any viewport aspect, any sort order.
   Please do not reintroduce per-card depths.
   ================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isNarrow = function () { return window.matchMedia('(max-width: 899px)').matches; };

  /* The room has two forms: the drifting scatter, and a static hang.
     Below 900px the scatter does not survive the viewport; under
     reduced motion it must not drift at all. Both end up in the same
     static hang, so they are asked about together — every gate below
     that used to read isNarrow() reads this instead.

     This matters for more than taste. A reduced-motion visitor used to
     keep the pinned stage with the drift frozen at zero, so the wall
     never advanced and `overflow: hidden` sealed off everything past
     the first screenful: 6 of 28 portraits reachable, and the two
     newest breeds not reachable at all. */
  var isStatic = function () { return isNarrow() || reduced; };

  /* Per-mode accent, the reference's --univers-color idea. All three
     are watercolour pigments, not arbitrary UI hues.

     Each mode carries two values of the same pigment. `line` is the
     rules, cursors and fills — UI, so a 3:1 floor, which the bright cut
     clears. `deep` is for anything the accent has to sit BEHIND as a
     background for cream text, which is body copy at a 4.5:1 floor. The
     bright cut fails that: measured on the live page, cream on the sage
     came to 4.48:1. These deep cuts measure 5.2 / 5.1 / 6.0. */
  var ACCENTS = {
    gallery: { line: '#9C7E45', deep: '#7A6132' },
    name:    { line: '#7E8B6E', deep: '#5D6A4E' },
    newest:  { line: '#6B7C8A', deep: '#4F5D69' }
  };

  /* ---- Field geometry, all in vw ---------------------------------
     THREE columns, 25.2 wide, 5 apart, 7.2 clear at each edge.

     Five until 2026-08-19, then four, then three. The column COUNT is the
     only real knob for portrait size: N columns across 100vw cap a card at
     roughly (100 - margins - gutters)/N, so five capped near 17.6 and four
     near 20. Three allows up to ~27, which was tried and read as too big —
     see CARD_MAX_H for the reason. Settled at a 17.8-24 band.

     The gutters are what make cross-column overlap impossible, so they are
     load-bearing. What has to hold is that a card INCLUDING the box its
     tilt sweeps out never crosses into the next column — that makes overlap
     impossible at any catalogue size, viewport or sort order rather than
     merely unlikely. A wide card therefore gets a SHALLOW tilt, since the
     swept box grows as w*(cos r - 1 + ar*sin r). Measured at a worst-case
     1.6 aspect the column spans are 7.0-32.5, 37.3-62.0, 67.4-92.9, so the
     tightest gap is 4.79vw. */
  var COL_X     = [7.2, 37.4, 67.6];
  var COL_W     = 25.2;

  /* Each column starts at a different height so rows never line up
     across the room and it reads as a scatter, not a grid.

     No column starts above 14vw: at rest the top of the stage is where
     the sort buttons, the scroll hint and the commission link sit, and
     a column starting at 0 put a half-cropped portrait behind them. */
  var COL_START = [14, 28, 20];

  /* Per-column gap scaling. This is what creates the parallax: a
     column packed tighter is shorter, so it travels less over the same
     runway and reads as further away. Depth is DERIVED from column
     length below rather than hand-set, which is why it can never drift
     out of step with the layout. */
  var COL_GAP   = [0.92, 1.08, 0.84];

  /* Slot = a place on the wall: which column, how far into it, how
     wide, how tilted. Slots belong to the ROOM, not to the artwork —
     sorting changes which portrait hangs in which slot, so the
     composition survives a re-sort. Widths stay in a 17.8-24vw band, and the
     wide end of it is only reachable at a shallow tilt — see the geometry
     note above. */
  var SLOTS = [
    { c: 0, jx: 0.6, w: 24.0, r: -2 }, { c: 1, jx: 1.2, w: 19.9, r:  3 },
    { c: 2, jx: 0.4, w: 22.8, r: -2 }, { c: 0, jx: 2.0, w: 18.7, r:  4 },
    { c: 1, jx: 0.5, w: 23.5, r: -2 }, { c: 2, jx: 1.8, w: 19.6, r:  3 },
    { c: 0, jx: 0.9, w: 21.9, r: -3 }, { c: 1, jx: 2.2, w: 17.8, r:  4 },
    { c: 2, jx: 0.6, w: 24.0, r: -2 }, { c: 0, jx: 1.6, w: 20.6, r:  3 },
    { c: 1, jx: 0.8, w: 22.2, r: -3 }, { c: 2, jx: 2.4, w: 18.7, r:  4 },
    { c: 0, jx: 0.4, w: 23.1, r: -2 }, { c: 1, jx: 1.8, w: 20.3, r:  3 },
    { c: 2, jx: 1.0, w: 21.3, r: -3 }
  ];

  /* A card is sized in vw but has to FIT a stage measured in vh, and those
     two have no fixed relationship. A 1.5 portrait — half this catalogue —
     at 24vw is 36vw tall, which is 62% of the stage on a 1440x900 window
     but 83% at 1920x900 and 91% on a 2560x1080 ultrawide: the painting
     stops fitting on the screen at all, and every one of those is a real
     desktop. So the height is capped at a fraction of the STAGE and the
     width derived back from it. On a tall window the slot width is already
     the smaller number and this changes nothing. */
  var CARD_MAX_H = 0.68;

  /* Vertical gaps, cycled. Varied so the column has a rhythm rather
     than a metronome, and generous because emptiness is the point of
     this room — 5-6 cards on screen at once, not a wall of thumbnails. */
  var GAPS = [14, 18, 12, 16, 13, 20, 15, 17];

  /* ---- The stroke -------------------------------------------------
     Skiper UI's "Skiper19 — the stroke that follows the scroll progress"
     (skiper-ui.com/v1/skiper19, by @gurvinder-singh02, free with
     attribution). This is their own path data, verbatim.

     Their curve opens with a dense knot of loops, and that knot is the
     "tangle". It is not styled away or faded out — it is CUT OFF the
     geometry before the path is ever built (see trimKnot), so there is
     nothing there to become visible at any scroll position. What is kept
     is the tail: the long sweeping curves the knot unwinds into.

     KNOT_Y is where the tail starts in their coordinate space. Their
     path climbs and dives repeatedly below this line; 561.655 is the
     exact endpoint at which the last loop finishes and the first long
     sweep begins. */
  var SKIPER_PATH = 'M876.605 394.131C788.982 335.917 696.198 358.139 691.836 416.303C685.453 501.424 853.722 498.43 941.95 409.714C1016.1 335.156 1008.64 186.907 906.167 142.846C807.014 100.212 712.699 198.494 789.049 245.127C889.053 306.207 986.062 116.979 840.548 43.3233C743.932 -5.58141 678.027 57.1682 672.279 112.188C666.53 167.208 712.538 172.943 736.353 163.088C760.167 153.234 764.14 120.924 746.651 93.3868C717.461 47.4252 638.894 77.8642 601.018 116.979C568.164 150.908 557 201.079 576.467 246.924C593.342 286.664 630.24 310.55 671.68 302.614C756.114 286.446 729.747 206.546 681.86 186.442C630.54 164.898 492 209.318 495.026 287.644C496.837 334.494 518.402 366.466 582.455 367.287C680.013 368.538 771.538 299.456 898.634 292.434C1007.02 286.446 1192.67 309.384 1242.36 382.258C1266.99 418.39 1273.65 443.108 1247.75 474.477C1217.32 511.33 1149.4 511.259 1096.84 466.093C1044.29 420.928 1029.14 380.576 1033.97 324.172C1038.31 273.428 1069.55 228.986 1117.2 216.384C1152.2 207.128 1188.29 213.629 1194.45 245.127C1201.49 281.062 1132.22 280.104 1100.44 272.673C1065.32 264.464 1044.22 234.837 1032.77 201.413C1019.29 162.061 1029.71 131.126 1056.44 100.965C1086.19 67.4032 1143.96 54.5526 1175.78 86.1513C1207.02 117.17 1186.81 143.379 1156.22 166.691C1112.57 199.959 1052.57 186.238 999.784 155.164C957.312 130.164 899.171 63.7054 931.284 26.3214C952.068 2.12513 996.288 3.87363 1007.22 43.58C1018.15 83.2749 1003.56 122.644 975.969 163.376C948.377 204.107 907.272 255.122 913.558 321.045C919.727 385.734 990.968 497.068 1063.84 503.35C1111.46 507.456 1166.79 511.984 1175.68 464.527C1191.52 379.956 1101.26 334.985 1030.29 377.017C971.109 412.064 956.297 483.647 953.797 561.655C947.587 755.413 1197.56 941.828 936.039 1140.66C745.771 1285.32 321.926 950.737 134.536 1202.19C-6.68295 1391.68 -53.4837 1655.38 131.935 1760.5C478.381 1956.91 1124.19 1515 1201.28 1997.83C1273.66 2451.23 100.805 1864.7 303.794 2668.89';
  var KNOT_Y = 560;

  /* The thread has no visible beginning and no visible end: it comes out
     from under the sticky header and runs away under the finale. The
     drawing is taller than the stage at both ends, so neither end is in
     frame: the stage clips the top, and the stage bottom is exactly where
     the finale panel begins, so the line reads as passing behind
     "Commission a painting" rather than stopping at it.

     More hangs below than above. The top only has to clear the header for
     the line to read as coming out from behind it; the bottom has to hold
     the whole of the final dive, because that is what lets the head outrun
     the scroll and be gone before the finale arrives instead of stopping
     dead on its edge. Painting always finishes at p=1, so where the head
     leaves the frame is set by OVER_BOTTOM alone — one knob, not two
     fighting each other.

     Where painting STARTS is measured, not set: the path is walked for the
     point that crosses into the stage. A hard-coded 0.02 left a visible
     stub in the top corner that then had to race sideways to catch up with
     the scroll. */
  var OVER_TOP    = 0.10;   // of stage height, hidden behind the header
  var OVER_BOTTOM = 0.22;   // of stage height, hidden under the finale
  var ENTER_PX    = 36;     // how far past the top edge the head sits at rest
  var THREAD_H    = 3.2;    // stage heights of drawing: how much there is to scroll
  var SMOOTH      = 0.09;   // the camera follows the head averaged over this much path
  var CENTRE      = 0.75;   // where down the stage the band sits (0.48 until 2026-10-06: the branch's tip rides at three quarters)

  /* THE DRAWING MOVES ONLY UP, AND ONLY VERTICALLY.

     Horizontally it is nailed down — x is never corrected — so the head
     sweeps side to side and the painted line never swims across the
     screen. That was the original fault: a camera held the head at the
     horizontal centre while the stroke slid sideways underneath the
     portraits, which is exactly backwards.

     Vertically it scrolls, so the head stays in a band near the middle
     rather than running top to bottom of one fixed screen. The offset is
     a PURE FUNCTION of scroll — a lookup table built once — and never
     accumulated. A dead-zone camera is a ratchet: corrections only ever
     add up, so a scroll position has no single appearance and scrolling
     back up does not retrace. A table reverses exactly.

     Three things shape that table, and all three are load-bearing:
       - It follows a SMOOTHED head, not the head. Following the head
         itself pins it dead centre and reads as a gunsight; the smoothing
         is what leaves it a band to breathe in.
       - It is monotonic. The curve climbs back up in three places, and a
         camera that followed would scroll the drawing DOWN while the page
         scrolled up. A running minimum holds it still there instead and
         lets the head ride up through the band on its own.
       - It is clamped at both ends, which is what keeps the thread with
         no visible beginning or end: at rest the clamp holds the drawing
         high so the line comes out from under the header, and at the
         finish it holds it low so the head runs off under the finale.

     Fitting the raw curve into the stage leaves a narrow ribbon — it is
     half again as tall as it is wide and the stage is the other way
     round — so the head would only ever cross a third of the screen.
     The GEOMETRY is stretched horizontally instead (see pathData), not
     the SVG: `preserveAspectRatio="none"` scales x and y by different
     factors, and a stroke cannot have two widths, so the line would
     render thin on its vertical runs and thick on its horizontal ones.
     Stretching the coordinates keeps the fit uniform.

     The cap is for ultrawides, and it has to clear the ordinary desktop
     or it bites on every laptop instead: a 16:9 window under the header
     is about 1.94 to 1, which needs 3.16. At 3 the stroke sat in the
     middle 74% of the screen on the commonest monitor there is. */
  var X_STRETCH_MAX = 3.6;
  var FIT = 0.96;              // of the stage, leaving the round caps clear

  var STROKE_PX = 9;
  var AMBER = '#B4823A';

  /* ---- How long the room is ---------------------------------------
     The runway was a fixed 420vh from the theme settings. That is a
     promise about the SCROLL, not about the catalogue, and the two
     come apart as soon as portraits are added: measureField already
     guarantees the last card clears the top by p=1, so a frozen scroll
     length does not hide anything — it just makes the whole wall move
     faster every time the collection grows. This shop grows daily.

     Measured on the live wall: 28 cards drift 4028px across 2880px of
     scroll, 1.40px of wall per px of scroll. The same 420vh at thirty
     breeds gives 6.34px per px, and at sixty, 12.67 — a blur, with
     every portrait on it for a fraction of a second.

     So length is DERIVED, exactly as the column depths are: the scroll
     span is held at a constant fraction of the drift, which keeps the
     pace identical whatever hangs on the wall. PACE is that fraction,
     taken from the 420vh room as it was tuned and approved at 28
     cards — so at today's catalogue this changes nothing at all, and
     from tomorrow's it changes everything. The theme setting survives
     as a floor, applied in CSS, for a shop with two or three
     portraits. */
  var PACE = 2880 / 4028;

  /* The scroll hint has done its job the moment you scroll, so it fades out
     almost immediately and comes back at the top — a prompt, not a
     dismissal.

     Measured against the VIEWPORT, not the runway. Everything else in this
     file derives from the wall on purpose, but this must not: the runway
     grows every time a portrait is added, and a fraction-of-runway fade
     would have the hint linger a little longer every month — 56px today,
     but half a screen at a catalogue of two hundred. How far you scroll
     before a prompt gets out of the way should not depend on how much
     stock the shop carries. */
  var HINT_HOLD = 0.012;   // of viewport height: still fully there
  var HINT_GONE = 0.085;   // of viewport height: fully faded

  function init() {
    var salon = document.querySelector('[data-salon]');
    if (!salon) return;

    var runway = salon.querySelector('.salon-runway');
    var field  = salon.querySelector('[data-salon-field]');
    var cards  = Array.prototype.slice.call(salon.querySelectorAll('.salon-card'));
    var index  = salon.querySelector('[data-salon-index]');
    var items  = index ? Array.prototype.slice.call(index.children) : [];
    var thread = salon.querySelector('[data-salon-thread]');
    var hint   = salon.querySelector('.salon-hint');
    if (!field || !cards.length) return;

    var mode = 'gallery';
    var colDepth = [1, 1, 1];

    /* ---- Layout --------------------------------------------------
       Stacks each column from real aspect ratios, so a landscape
       Labrador and a portrait Cavalier both get their true height and
       the gap below them is honest. Runs off data-ar rather than
       measured pixels, so it is correct before the images decode and
       never reflows when they arrive. */
    function layout(order) {
      var cursors = COL_START.slice();

      /* Slot widths are a ceiling, not a size: see CARD_MAX_H. Measured
         once per layout, not per card. */
      var vwPx  = window.innerWidth / 100;
      var stgPx = salon.querySelector('.salon-stage').getBoundingClientRect().height;
      var maxH  = stgPx > 0 ? CARD_MAX_H * (stgPx / vwPx) : Infinity;

      order.forEach(function (card, i) {
        var slot = SLOTS[i % SLOTS.length];
        var col  = slot.c;
        var ar   = parseFloat(card.dataset.ar) || 1.4;
        var w    = Math.min(slot.w, maxH / ar);
        var h    = w * ar;
        /* Centre a capped card on the footprint its slot gave it, so the
           wall keeps its rhythm instead of everything shuffling left. */
        var jx   = slot.jx + (slot.w - w) / 2;

        card.style.setProperty('--x', (COL_X[col] + jx).toFixed(2) + 'vw');
        card.style.setProperty('--y', cursors[col].toFixed(2) + 'vw');
        card.style.setProperty('--w', w.toFixed(2) + 'vw');
        card.style.setProperty('--r', slot.r + 'deg');
        // Staggered start, so a re-sort reads as the room rearranging
        // itself rather than every portrait jumping at once.
        card.style.setProperty('--sd', (i * 14) + 'ms');
        card.dataset.col = col;

        cursors[col] += h + GAPS[i % GAPS.length] * COL_GAP[col];
      });

      /* Depth from column length. The longest column travels its own
         length over the runway (depth 1.04); shorter ones travel
         proportionally less, so nothing outruns anything and every
         column stays populated to the end. The 0.78-1.04 band is the
         one that was tuned to look right — only its ASSIGNMENT
         changed, from per-card to per-column. */
      var longest = Math.max.apply(null, cursors);
      cursors.forEach(function (len, c) {
        colDepth[c] = longest > 0 ? 0.78 + 0.26 * (len / longest) : 1;
      });
      cards.forEach(function (card) {
        card.dataset.d = colDepth[card.dataset.col | 0].toFixed(4);
      });

      /* Toggle, not add: the static hang needs this class GONE, not
         merely overridden. Mobile could get away with add() because the
         absolute positioning it turns on is inside a min-width query,
         but reduced motion has no such query to hide behind. */
      field.classList.toggle('is-laid-out', !isStatic());
      driftRange = 0;         // force a re-measure on the next frame
      buildThread();
    }

    /* ---- Sorting -------------------------------------------------
       The buttons rearrange the WALL, not just the list beside it.
       Slots stay put; portraits move between them. */
    function orderedCards() {
      var list = cards.slice();
      if (mode === 'name') {
        list.sort(function (a, b) {
          return a.dataset.breed.localeCompare(b.dataset.breed) ||
                 (a.dataset.round - b.dataset.round);
        });
      } else if (mode === 'newest') {
        list.sort(function (a, b) {
          return (b.dataset.published - a.dataset.published) ||
                 a.dataset.breed.localeCompare(b.dataset.breed) ||
                 (a.dataset.round - b.dataset.round);
        });
      } else {
        list.sort(function (a, b) { return a.dataset.order - b.dataset.order; });
      }
      return list;
    }

    /* hp-brand.css transitions left/top/width/--r, but only while
       `.is-shuffling` is set — a permanent transition would also animate
       the very first layout and every resize. So the class goes on for
       exactly one re-sort and comes straight back off.

       `booted` is what keeps it off that first layout: setMode runs once
       during init to establish gallery order, and nothing should fly in
       from nowhere on load. */
    var booted = false, shuffleTimer = null;

    function setMode(next) {
      mode = next;

      if (booted) {
        field.classList.add('is-shuffling');
        clearTimeout(shuffleTimer);
        // Longest transition (900ms) plus the last card's stagger.
        shuffleTimer = setTimeout(function () {
          field.classList.remove('is-shuffling');
        }, 900 + cards.length * 14 + 60);
      }

      var acc = ACCENTS[mode] || ACCENTS.gallery;
      salon.style.setProperty('--room-accent', acc.line);
      salon.style.setProperty('--room-accent-deep', acc.deep);

      Array.prototype.forEach.call(salon.querySelectorAll('.salon-mode'), function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.mode === mode));
      });

      layout(orderedCards());

      if (!index) return;
      var sorted = items.slice();
      if (mode === 'name') {
        sorted.sort(function (a, b) { return a.dataset.breed.localeCompare(b.dataset.breed); });
      } else if (mode === 'newest') {
        sorted.sort(function (a, b) {
          return (b.dataset.published - a.dataset.published) ||
                 a.dataset.breed.localeCompare(b.dataset.breed);
        });
      } else {
        sorted.sort(function (a, b) { return a.dataset.order - b.dataset.order; });
      }
      sorted.forEach(function (li) { index.appendChild(li); });
      items = sorted;
      /* Nothing to re-anchor: the focused row is a function of scroll
         position, so paintIndex recomputes it on the next frame and the
         column stays wherever the page is. */
    }

    Array.prototype.forEach.call(salon.querySelectorAll('.salon-mode'), function (btn) {
      btn.addEventListener('click', function () { setMode(btn.dataset.mode); });
    });

    /* ---- The stroke ----------------------------------------------
       See the notes at the top of this file for what is borrowed from
       Skiper19 and what is generated instead, and why. */
    var threadPath = null;
    var drawFrom = 0, drawTo = 1;   // measured in buildThread
    var cam = [];                   // vertical offset per path fraction

    /* Stage size at the last build. The stroke no longer depends on the
       card layout, so a re-sort must not rebuild it — only a stage that
       genuinely changed size may. */
    var thStageW = 0, thStageH = 0;

    function buildThread(force) {
      if (!thread || isStatic()) return;

      var stage = salon.querySelector('.salon-stage');
      var rect = stage.getBoundingClientRect();
      /* The entry gate can still be over the page at boot, and the
         static room hides the SVG outright — getBBox would measure
         nothing either way. Bail; load and resize come back for it. */
      if (rect.width < 2 || rect.height < 2) return;
      if (threadPath && !force &&
          Math.abs(rect.width - thStageW) < 1 &&
          Math.abs(rect.height - thStageH) < 1) return;
      thStageW = rect.width;
      thStageH = rect.height;

      /* Cut the knot off the front of their path.

         Walked segment by segment: everything up to the point where the
         curve finally settles below KNOT_Y is discarded, and the path is
         restarted there. Trimming the geometry rather than hiding it
         means the tangle cannot reappear at any scroll position, any
         viewport, or any draw fraction. */
      var m = SKIPER_PATH.match(/^M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/);
      var cur = [parseFloat(m[1]), parseFloat(m[2])];
      var parts = SKIPER_PATH.slice(m[0].length).split('C');
      var kept = null, start = null, i, n;

      for (i = 0; i < parts.length; i++) {
        if (!parts[i].trim()) continue;
        n = parts[i].trim().split(/[\s,]+/).map(Number);
        if (kept === null && n[5] > KNOT_Y) { start = cur; kept = []; }
        if (kept !== null) kept.push(n);
        cur = [n[4], n[5]];
      }
      if (!kept || !kept.length) { start = cur; kept = []; }

      /* Draw it once at true proportions to find out what shape the trim
         left behind, then again stretched to the stage. getBBox is the
         only honest measure here — their path deliberately runs outside
         its stated viewBox. */
      thread.innerHTML =
        '<path d="' + pathData(start, kept, 0, 1) + '" fill="none" pathLength="1" ' +
        'stroke="' + AMBER + '" stroke-linecap="round" stroke-linejoin="round"></path>';
      threadPath = thread.querySelector('path');

      var bb = threadPath.getBBox();
      if (!bb.width || !bb.height) { threadPath = null; return; }

      /* Stretch to the BOX the curve is fitted into — THREAD_H stage
         heights of drawing — not to the stage itself. */
      var elW = rect.width * FIT, elH = rect.height * THREAD_H;
      var k = Math.max(1, Math.min(X_STRETCH_MAX,
        (elW / elH) / (bb.width / bb.height)));
      threadPath.setAttribute('d', pathData(start, kept, bb.x + bb.width / 2, k));

      bb = threadPath.getBBox();
      var pad = bb.height * 0.02;               // room for the round caps
      var vbW = bb.width + pad * 2, vbH = bb.height + pad * 2;

      thread.setAttribute('viewBox',
        (bb.x - pad) + ' ' + (bb.y - pad) + ' ' + vbW + ' ' + vbH);
      /* `meet` does the uniform contain-fit. The stretch above already
         matched the two aspects, so it fills rather than letterboxes. */
      thread.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      thread.style.width  = elW.toFixed(1) + 'px';
      thread.style.height = elH.toFixed(1) + 'px';
      thread.style.left   = ((rect.width  - elW) / 2).toFixed(1) + 'px';
      thread.style.top    = '0px';              // the transform does the placing


      /* Stroke width in USER units, deliberately.
         `vector-effect="non-scaling-stroke"` cannot be used: it resolves
         the stroke — and with it the dash pattern — in screen space,
         while the dash values here are normalised path units. Mixing the
         two once made this draw a fourteenth of the line. */
      var scale = Math.min(elW / vbW, elH / vbH);     // px per user unit
      threadPath.setAttribute('stroke-width', (STROKE_PX / scale).toFixed(4));

      /* pathLength="1" normalises the path to a single unit, so the dash
         value is the plain 0-1 fraction Framer's `pathLength` gives you. */
      threadPath.style.strokeDasharray = '1';

      /* ---- The camera table ---------------------------------------
         `meet` letterboxes whenever the stretch was capped, so the drawing
         is NOT the element box: measure the content and centre it the way
         xMidYMid did, or every clamp below is out by half the slack. */
      var contentH = vbH * scale, contentTop = (elH - contentH) / 2;
      var len = threadPath.getTotalLength(), vbY = bb.y - pad, S = 600, sy = [], j;
      for (j = 0; j <= S; j++) {
        sy.push(contentTop + (threadPath.getPointAtLength(len * j / S).y - vbY) * scale);
      }

      /* Follow the head averaged over a window, not the head itself, so
         it keeps a band instead of sitting on a crosshair. */
      var W = Math.max(1, Math.round(S * SMOOTH)), band = CENTRE * rect.height;
      var lo = rect.height * (1 + OVER_BOTTOM) - contentTop - contentH;  // bottom stays covered
      var hi = -rect.height * OVER_TOP - contentTop;                     // top stays covered
      var run = 0, a, b, sum, n;
      cam = [];
      for (j = 0; j <= S; j++) {
        a = Math.max(0, j - W); b = Math.min(S, j + W);
        for (sum = 0, n = a; n <= b; n++) sum += sy[n];
        var c = band - sum / (b - a + 1);
        if (c > hi) c = hi; else if (c < lo) c = lo;
        /* Never let it travel back DOWN the screen: where the curve
           climbs, the drawing holds and the head rides up the band. */
        if (j === 0) run = c; else if (c < run) run = c;
        cam.push(run);
      }

      /* At rest the clamp holds the drawing high, so the line is already
         coming out from under the header. Start painting where it has
         just cleared the top edge. */
      drawFrom = 0;
      for (j = 0; j <= S; j++) if (sy[j] + cam[j] >= ENTER_PX) { drawFrom = j / S; break; }
      drawTo = 1;      // the tail below the stage is drawn too, unseen

      threadPath.style.strokeDashoffset = reduced ? 0 : (1 - drawFrom);
    }

    /* Stretch x about `cx` by `k` and leave y alone. Affine, so the
       curve keeps its tangents and cannot gain a cusp. */
    function pathData(start, kept, cx, k) {
      var x = function (v) { return (cx + (v - cx) * k).toFixed(2); };
      var d = 'M ' + x(start[0]) + ' ' + start[1];
      for (var i = 0; i < kept.length; i++) {
        var n = kept[i];
        d += ' C ' + x(n[0]) + ' ' + n[1] +
             ' '   + x(n[2]) + ' ' + n[3] +
             ' '   + x(n[4]) + ' ' + n[5];
      }
      return d;
    }

    /* Paint `frac` of the stroke and lift the drawing to the offset the
       table holds for it. Both are functions of `frac` alone, so a scroll
       position has exactly one appearance and scrolling back up un-paints
       along the same line, over the same ground. */
    function drawThread(frac) {
      if (!threadPath) return;
      threadPath.style.strokeDashoffset = (1 - frac).toFixed(4);
      if (!cam.length) return;
      var t = frac * (cam.length - 1);
      var i0 = Math.floor(t), i1 = Math.min(cam.length - 1, i0 + 1);
      var y = cam[i0] + (cam[i1] - cam[i0]) * (t - i0);
      thread.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
    }

    /* ---- Dust ----------------------------------------------------
       Small rotating diamonds, the reference's drifting motes. */
    var dust = salon.querySelector('.salon-dust');
    var dctx = null, motes = [], dw = 0, dh = 0, dpr = 1;

    function seedDust() {
      if (!dust || reduced || isNarrow()) { motes = []; return; }
      dctx = dctx || dust.getContext('2d');
      if (!dctx) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      dw = dust.clientWidth; dh = dust.clientHeight;
      dust.width = Math.round(dw * dpr); dust.height = Math.round(dh * dpr);
      dctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var count = Math.round(Math.min(34, Math.max(12, dw / 46)));
      motes = [];
      for (var i = 0; i < count; i++) {
        motes.push({
          x: Math.random() * dw, y: Math.random() * dh,
          r: 3 + Math.random() * 7,
          rot: Math.random() * Math.PI,
          spin: (Math.random() - 0.5) * 0.004,
          vx: (Math.random() - 0.5) * 0.16,
          vy: 0.04 + Math.random() * 0.16,
          a: 0.05 + Math.random() * 0.13
        });
      }
    }

    function drawDust() {
      if (!dctx || !motes.length) return;
      dctx.clearRect(0, 0, dw, dh);
      for (var i = 0; i < motes.length; i++) {
        var m = motes[i];
        m.x += m.vx; m.y += m.vy; m.rot += m.spin;
        if (m.y - m.r > dh) { m.y = -m.r; m.x = Math.random() * dw; }
        if (m.x < -m.r) m.x = dw + m.r;
        if (m.x > dw + m.r) m.x = -m.r;
        dctx.save();
        dctx.translate(m.x, m.y);
        dctx.rotate(m.rot + Math.PI / 4);   // squares on point = diamonds
        dctx.globalAlpha = m.a;
        dctx.fillStyle = '#6E6659';
        dctx.fillRect(-m.r / 2, -m.r / 2, m.r, m.r);
        dctx.restore();
      }
    }

    /* ---- Drift + lean -------------------------------------------- */
    var leanTX = 0, leanTY = 0, leanX = 0, leanY = 0;

    if (!reduced) {
      window.addEventListener('mousemove', function (e) {
        leanTX = (e.clientX / window.innerWidth - 0.5) * 2;   // -1..1
        leanTY = (e.clientY / window.innerHeight - 0.5) * 2;
      }, { passive: true });
    }

    /* Pixels scrolled into the runway, stashed by progress() because it
       already has the rect and a second read per frame would be waste. */
    var scrolledPx = 0;

    function progress() {
      var r = runway.getBoundingClientRect();
      var span = r.height - window.innerHeight;
      scrolledPx = -r.top;
      if (span <= 0) return 0;
      var p = -r.top / span;
      return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    /* ---- Drift range, measured not guessed -----------------------
       A shop with twelve portraits only fills part of the last column,
       so the furthest card sits short of where a hard-coded range
       would assume. Hard-coding it flings everything off the top long
       before the runway ends and the room stands empty for the rest of
       the scroll. Measure instead: for each card work out the drift it
       needs to just clear the top by p=1, and take the largest. */
    var driftRange = 0;

    function measureField() {
      driftRange = 0;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        var d = parseFloat(c.dataset.d) || 1;
        var need = (c.offsetTop + c.offsetHeight) / d;
        if (need > driftRange) driftRange = need;
      }
      sizeRunway();

    }


    /* Grow the room to fit the wall. Runs off driftRange, so it is
       correct for whatever measureField just found — and driftRange is
       measured from offsetTop inside the field, which the runway's own
       height does not affect, so there is no loop here.

       The static room is left alone: it has no drift to pace, and CSS
       gives it `height: auto` so it is exactly as tall as its
       portraits. */
    function sizeRunway() {
      if (isStatic()) { runway.style.removeProperty('--salon-runway'); return; }
      if (!driftRange) return;
      runway.style.setProperty(
        '--salon-runway',
        Math.round(window.innerHeight + driftRange * PACE) + 'px'
      );
    }

    /* ---- The index as a focus list -------------------------------
       Whatever is passing the middle of the screen sits centred at
       full strength; its neighbours fade; anything further is gone.
       `focus` is a float lerped toward the active row, so the column
       glides rather than snapping between names. */
    var focus = 0, focusTarget = 0;
    var hintOpacity = 1;            // last value written to .salon-hint

    /* The index is STILL unless the pointer is over it.

       It has now been driven three ways. Following the nearest card made
       it flicker, because cards drift at five different column speeds.
       Following page scroll made it travel, but it moved whenever the
       page did, which is most of the time. It now moves only when it is
       being read: hover the column and the wheel walks the names;
       take the pointer away and it holds exactly where it was left.

       The wheel is captured only while there is somewhere left to go —
       at either end the event is left alone so the page scrolls on
       normally instead of the pointer feeling stuck. */
    var indexHot = false, reach = 2.5;
    var indexHost = salon.querySelector('.salon-index');

    if (indexHost) {
      indexHost.addEventListener('mouseenter', function () { indexHot = true; });
      indexHost.addEventListener('mouseleave', function () { indexHot = false; });

      indexHost.addEventListener('wheel', function (e) {
        if (isStatic() || items.length < 2) return;
        var last = items.length - 1;
        var next = focusTarget + (e.deltaY > 0 ? 1 : -1) * 0.5;

        // Only swallow the wheel while the list can still move.
        if ((next < 0 && focusTarget <= 0) || (next > last && focusTarget >= last)) return;

        e.preventDefault();
        focusTarget = Math.max(0, Math.min(last, next));
      }, { passive: false });
    }

    function paintIndex() {
      if (!index || !items.length) return;
      // A static room has no sticky stage and so no centre line to
      // track; the index is a plain list and CSS owns it.
      if (isStatic()) return;

      focus += (focusTarget - focus) * 0.16;

      var rowH = items[0].offsetHeight || 1;
      index.style.transform = 'translate3d(0,' + (-focus * rowH).toFixed(2) + 'px,0)';

      var centre = Math.round(focus);
      /* Reach 2.5 rows normally — one name above and one below stay
         readable and the rest dissolve. While the pointer is on the
         column you are reading it rather than glancing at it, so the
         falloff opens out and more of the collection is legible. */
      reach += ((indexHot ? 4.2 : 2.5) - reach) * 0.12;   // eased, not switched
      for (var i = 0; i < items.length; i++) {
        var dist = Math.abs(i - focus);
        var t = 1 - dist / reach;
        items[i].style.opacity = t <= 0 ? 0 : Math.pow(t, 1.7).toFixed(3);
        items[i].classList.toggle('is-active', i === centre);
      }
    }

    function frame() {
      var p = reduced ? 0 : progress();

      if (!isStatic()) {
        var vh = window.innerHeight;
        if (!driftRange) measureField();
        var range = driftRange;

        leanX += (leanTX - leanX) * 0.06;
        leanY += (leanTY - leanY) * 0.06;

        for (var i = 0; i < cards.length; i++) {
          var c = cards[i];
          var d = parseFloat(c.dataset.d) || 1;
          var ty = -p * range * d;
          var lx = leanX * 26 * d;
          var ly = leanY * 14 * d;
          c.style.transform =
            'translate3d(' + lx.toFixed(2) + 'px,' + (ty + ly).toFixed(2) + 'px,0) ' +
            'rotate(var(--r, 0deg))';
        }

        if (threadPath && !reduced) {
          /* Drawn fraction comes straight off scroll, so the stroke is
             painted at exactly the rate the page moves — and unpainted
             at exactly that rate on the way back up. */
          drawThread(drawFrom + (drawTo - drawFrom) * p);
        }

        /* Written only when it actually changes: this runs every frame and
           the value is constant for all but a sliver of the scroll. */
        if (hint) {
          var vhh = window.innerHeight;
          var ho = 1 - (scrolledPx - HINT_HOLD * vhh) / ((HINT_GONE - HINT_HOLD) * vhh);
          ho = ho < 0 ? 0 : (ho > 1 ? 1 : ho);
          if (Math.abs(ho - hintOpacity) > 0.004) {
            hintOpacity = ho;
            hint.style.opacity = ho.toFixed(3);
          }
        }
      }

      /* The static room never fades it, so a desktop visitor who scrolled
         and then narrowed the window would otherwise be left with an
         inline opacity:0 on a hint that is now part of normal flow. */
      if (isStatic() && hint && hintOpacity !== 1) {
        hintOpacity = 1;
        hint.style.opacity = '';
      }

      paintIndex();
      drawDust();
      requestAnimationFrame(frame);
    }

    /* Lay the room out before anything is measured. On the homepage the
       entry gate is still covering the page at this point, so the first
       layout pass is never seen. */
    setMode('gallery');
    booted = true;   // from here on, a re-sort animates
    seedDust();
    measureField();
    focus = focusTarget = 0;

    // Slot heights come from data-ar, so nothing should move when the
    // artwork decodes — but re-measure anyway in case a src 404s and a
    // card collapses.
    /* A late header measurement changes the stage height, and the
       stroke is fitted to the stage, so re-fit once the page is up. */
    window.addEventListener('load', function () { measureField(); buildThread(); });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        seedDust();
        /* The height cap is a fraction of the STAGE, so a resize changes
           every card width. Re-lay before re-measuring, or driftRange and
           the runway are derived from the old geometry. */
        layout(orderedCards());
        buildThread(true);
        measureField();
      }, 180);
    }, { passive: true });

    requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
